/**
 * cache/solve.ts — forward reachability over a mission graph.
 *
 * Because the loop is always ungated and every key sits on it (mission.ts), a cycle
 * built by the library is solvable BY CONSTRUCTION. This module therefore exists as an
 * ASSERTION, not a filter: at runtime a failure means reroll with the next sub-seed,
 * and in tests a failure is a hard error — a cycle definition is wrong, and no amount
 * of rerolling will fix it. That is the Diablo 1 "lockout" check (flood-fill, and if
 * the level is not traversable throw it away) with the pass/fail meaning inverted,
 * because we expect to pass.
 */
import type { MissionGraph } from "./mission.js";

export interface MissionSolution {
  ok: boolean;
  /** "" when ok; otherwise what was unreachable. */
  reason: string;
  /** Node indices reachable from the entrance, keys accounted for. */
  reached: Set<number>;
  /** Distinct keys the run requires. */
  keys: number;
  /** Most locked edges crossed in series on the cheapest route to the boss. */
  gateDepth: number;
}

/**
 * Expand from the entrance to a fixpoint: a locked edge opens once its key's room is
 * itself reached, which can unlock further edges, so this iterates until nothing new
 * appears. `drop` edges traverse a→b only.
 */
export function solveMission(m: MissionGraph): MissionSolution {
  const reached = new Set<number>([m.entrance]);
  // Gates crossed on the cheapest route to each reached node.
  const depth = new Map<number, number>([[m.entrance, 0]]);

  let grew = true;
  while (grew) {
    grew = false;
    for (const e of m.edges) {
      const locked = e.lock >= 0;
      if (locked && !reached.has(e.lock)) continue; // key not in hand yet
      const step = locked ? 1 : 0;
      const dirs: [number, number][] = e.oneWay ? [[e.a, e.b]] : [[e.a, e.b], [e.b, e.a]];
      for (const [from, to] of dirs) {
        if (!reached.has(from)) continue;
        const cand = (depth.get(from) ?? 0) + step;
        if (!reached.has(to)) {
          reached.add(to);
          depth.set(to, cand);
          grew = true;
        } else if (cand < (depth.get(to) ?? Infinity)) {
          depth.set(to, cand);
          grew = true;
        }
      }
    }
  }

  const keys = new Set(m.edges.filter((e) => e.lock >= 0).map((e) => e.lock));
  let reason = "";
  if (!reached.has(m.boss)) reason = "boss unreachable";
  else {
    for (const n of m.nodes) {
      if ((n.role === "treasure" || n.role === "key") && !reached.has(n.i)) {
        reason = `${n.role} node ${n.i} unreachable`;
        break;
      }
    }
  }

  return {
    ok: reason === "",
    reason,
    reached,
    keys: keys.size,
    gateDepth: depth.get(m.boss) ?? -1,
  };
}
